import { dispatcher } from 'dispatcher';
import { useMemo } from 'react';
import { useChangePlayerFavorites, useGetPlayerFavorites } from 'server';

import * as hooks from './hooks';

export const useGamePublicAPI = () => {
    const getGamesConfiguration = hooks.useGetGamesConfiguration();
    const getPlayerFavorites = useGetPlayerFavorites();
    const changePlayerFavorites = useChangePlayerFavorites();
    const getOldGamesFeeds = hooks.useGetOldGamesFeeds();
    const getGamesFeed = hooks.useGetGamesFeed();

    return useMemo(() => ({
        /**
         * Open a game according to launch configuration of its game type, if it exists.
         * Otherwise, do nothing.
         * @memberOf Playtech.API.game
         * @param params {Object}
         * @param params.code {String} game code that is configured in CMS
         * @param [params.mode=1] {Number} 1 - real mode, 0 - demo mode
         * @example
         * Playtech.API.game.open({ code: 'gameCode' });
         */
        open: ({ code, mode = 1 }: { code: string; mode?: 0 | 1 }) => dispatcher.dispatch('game:launch', { code, mode }),
        /**
         * Getting games configuration data.
         * @memberOf Playtech.API.game
         * @function
         * @param [disableCache] {Boolean} if true data will always be taken from server instead of storage
         * @returns {Promise<{Object}>}
         * @example
         * Playtech.API.game.getGamesConfiguration(true).then(console.log);
         */
        getGamesConfiguration,
        /**
         * Return player favorites.
         * @deprecated
         * @memberOf Playtech.API.game
         * @function
         * @param params {Object}
         * @param params.requestedFavorites {String[]} list of supported favorites. only 'games' is supported for now.
         * @example
         * Playtech.API.game.getPlayerFavorites({ requestedFavorites: ['games'] });
         */
        getPlayerFavorites,
        /**
         * Change player favorites.
         * @memberOf Playtech.API.game
         * @function
         * @param params {Object}
         * @param params.requestType {"add" | "remove"} type of the operation.
         * @param params.gameTypes {String[]} list of game codes to add to / remove from favorites.
         * @returns {Promise<{Object}>}
         * @example
         * Playtech.API.game.changePlayerFavorites({ gameTypes: ['manos'], requestType: 'add' });
         */
        changePlayerFavorites,
        /**
         * Getting all old games feeds.
         * @function
         * @deprecated
         * @memberOf Playtech.API.game
         * @param [disableCache] {Boolean} if true data will always be taken from server instead of storage
         * @returns {Promise<{Array}>}
         * @example
         * Playtech.API.game.getOldGamesFeeds(true).then(console.log);
         */
        getOldGamesFeeds,
        /**
         * Getting games feed.
         * @memberOf Playtech.API.game
         * @function
         * @param feedName {String}
         * @param [disableCache] {Boolean} if true data will always be taken from server instead of storage
         * @returns {Promise<{Object}>}
         * @example
         * Playtech.API.game.getGamesFeed('design-feed', true).then(console.log);
         */
        getGamesFeed
    }), [
            getGamesFeed,
            getOldGamesFeeds,
            getPlayerFavorites,
            changePlayerFavorites,
            getGamesConfiguration
        ]
    );
};
